import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings, whatsappLink } from "@/lib/settings";
import { useSeo } from "@/lib/useSeo";

export default function Contact() {
  const s = useSettings();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const field = "w-full px-4 py-3 rounded-xl border border-[#DCE6EF] bg-white focus:outline-none focus:ring-2 focus:ring-[#2472C8]/40";

  useSeo({
    title: "Contatti | Edilstudio Immobiliare Scandicci e Firenze",
    description: "Contatta Edilstudio a Scandicci: telefono, email, WhatsApp e sede. Studio tecnico, agenzia immobiliare e amministrazioni condominiali.",
    canonicalPath: "/contact/",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Accetta l'informativa privacy per continuare.");
    setLoading(true);
    try {
      await api.post("/leads", { kind: "contatto", name: f.name, email: f.email, phone: f.phone, message: f.message, data: {} });
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setF({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore invio.");
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="bg-[#1B2A41] py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#F5A623]">Contatti</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mt-2">Parliamo del tuo progetto</h1>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Dove trovarci</h2>
          <ul className="mt-6 space-y-5">
            <li className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#2472C8] mt-0.5" /><span className="text-[#2C3A4F]">{s.address}</span></li>
            <li className="flex items-center gap-3"><Phone className="w-5 h-5 text-[#2472C8]" /><a href={`tel:${s.phone}`} className="text-[#2C3A4F] hover:text-[#2472C8]">{s.phone}</a></li>
            <li className="flex items-center gap-3"><Mail className="w-5 h-5 text-[#2472C8]" /><a href={`mailto:${s.email}`} className="text-[#2C3A4F] hover:text-[#2472C8]">{s.email}</a></li>
            <li className="flex items-start gap-3"><Clock className="w-5 h-5 text-[#2472C8] mt-0.5" /><span className="text-[#2C3A4F]">Lun–Ven 9:00–13:00 / 15:00–19:00</span></li>
          </ul>
          <a href={whatsappLink(s.whatsapp_number, "Salve Edilstudio, vorrei informazioni.")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3.5 rounded-full mt-8 hover:opacity-90 transition-opacity" data-testid="contact-whatsapp">Scrivici su WhatsApp</a>
          <div className="mt-8 rounded-2xl overflow-hidden border border-[#DCE6EF]">
            <iframe title="Mappa Edilstudio Scandicci" src="https://www.google.com/maps?q=Via+Salvador+Allende+14c+Scandicci+Firenze&output=embed" className="w-full h-64" loading="lazy" />
          </div>
        </div>

        <div>
          <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Inviaci un messaggio</h2>
            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={f.name} onChange={set("name")} placeholder="Nome e Cognome *" className={field} data-testid="contact-name" />
                <input required value={f.phone} onChange={set("phone")} placeholder="Telefono *" className={field} data-testid="contact-phone" />
              </div>
              <input required type="email" value={f.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="contact-email" />
              <textarea required value={f.message} onChange={set("message")} placeholder="Il tuo messaggio *" rows={5} className={field} data-testid="contact-message" />
              <label className="flex items-start gap-2 text-xs text-[#566476] leading-relaxed cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#2472C8]" data-testid="contact-consent" />
                Ho letto l'informativa privacy e acconsento al trattamento dei dati (GDPR).
              </label>
              <button type="submit" disabled={loading} className="w-full bg-[#1B2A41] hover:bg-[#24344F] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60 transition-colors" data-testid="contact-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Invia messaggio
              </button>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
